import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Lock } from "lucide-react";
import { useLeadGate, LeadGateForm } from "@/components/LeadGate";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import type { CalcResults } from "@/lib/calcEngine";

interface CashFlowSummaryProps {
  r: CalcResults;
  airbnbPlatformFeeRate: number;
  managementFeeRate: number;
  furnishingCost: number;
  managementOnly: boolean;
}

const CashFlowSummary = ({
  r,
  airbnbPlatformFeeRate,
  managementFeeRate,
  furnishingCost,
  managementOnly,
}: CashFlowSummaryProps) => {
  const { unlocked, unlock } = useLeadGate();
  const [open, setOpen] = useState(false);

  const blurClass = unlocked ? "" : "blur-sm select-none pointer-events-none";

  return (
    <Card className="border-primary/20 shadow-xl">
      <CardHeader className="bg-primary/5 pb-6">
        <CardTitle className="text-2xl text-center">
          Annual Cash Flow Summary
        </CardTitle>
        <CardDescription className="text-center">
          Estimated projections (Annual & Monthly Avg)
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-6 space-y-6">
        <div className={`space-y-3 ${blurClass}`}>
          <div className="space-y-3">
            <div className="flex justify-between items-center text-base">
              <span className="text-muted-foreground font-medium">
                Annual Gross Revenue
              </span>
              <span className="font-bold text-foreground">
                ${r.totalAnnualIncome.toLocaleString()}
              </span>
            </div>
            <div className="text-[11px] text-muted-foreground flex justify-between">
              <span>Monthly Average:</span>
              <span className="font-semibold text-foreground">
                ${r.totalMonthlyIncome.toLocaleString()}/mo
              </span>
            </div>

            <div className="flex justify-between items-center text-xs text-muted-foreground pt-1">
              <span>Airbnb Service Fee ({airbnbPlatformFeeRate * 100}%)</span>
              <span className="text-destructive">
                -${Math.round(r.annualAirbnbPlatformFee).toLocaleString()}/yr
              </span>
            </div>
            <div className="flex justify-between items-center text-xs text-muted-foreground">
              <span>Co-Hosting Fee ({managementFeeRate * 100}%)</span>
              <span className="text-destructive">
                -${Math.round(r.annualManagementFee).toLocaleString()}/yr
              </span>
            </div>

            <div className="flex justify-between items-center text-base p-2.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
              <div>
                <div className="font-medium text-emerald-800">
                  Owner Annual Net Payout
                </div>
                <div className="text-[11px] text-emerald-700">
                  ~${Math.round(r.monthlyGrossPayout).toLocaleString()}/mo avg
                </div>
              </div>
              <span className="font-bold text-emerald-600 text-lg">
                ${Math.round(r.annualGrossPayout).toLocaleString()}
              </span>
            </div>
          </div>

          <div className="space-y-2 pt-2 border-t border-border">
            <div className="flex justify-between items-center text-sm">
              <span className="text-muted-foreground">
                Annual Mortgage / Rent
              </span>
              <span className="text-destructive">
                -${r.annualMortgage.toLocaleString()}/yr
              </span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-muted-foreground text-xs leading-tight">
                Operating Expenses (Utilities, Mainte...)
              </span>
              <span className="text-destructive font-medium">
                -${r.annualOperatingExpenses.toLocaleString()}/yr
              </span>
            </div>
          </div>

          <div className="pt-4 border-t-2 border-border mt-4">
            <div className="flex flex-col items-center justify-center gap-1">
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Annual Net Cash Flow
              </span>
              <span
                className={`text-4xl md:text-5xl font-heading font-bold ${r.annualNetCashFlow >= 0 ? "text-emerald-600" : "text-destructive"} ${unlocked ? "" : "blur-lg select-none pointer-events-none"}`}
              >
                ${r.annualNetCashFlow.toLocaleString()}
              </span>
              <span className="text-xs font-medium text-muted-foreground mt-1">
                (~
                <strong
                  className={
                    r.monthlyNetCashFlow >= 0
                      ? "text-emerald-600"
                      : "text-destructive"
                  }
                >
                  ${r.monthlyNetCashFlow.toLocaleString()}
                </strong>{" "}
                / month avg)
              </span>
            </div>
          </div>

          {!managementOnly && (
            <div className="pt-6 mt-6 border-t-2 border-border">
              <h4 className="text-lg font-bold mb-4 text-center">
                Initial Investment
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Down Payment</span>
                  <span>${r.downPaymentAmount.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span>Closing Costs</span>
                  <span>${r.closingCostsAmount.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span>Furnishing & Setup</span>
                  <span>${furnishingCost.toLocaleString()}</span>
                </div>
                <div className="flex justify-between pt-2 border-t font-bold text-lg">
                  <span>Total Cash Needed</span>
                  <span className="text-primary">
                    ${r.totalAcquisitionCost.toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          )}

          <p className="text-[10px] text-muted-foreground text-center pt-4 italic">
            Disclaimer: These calculations are estimates for informational
            purposes only and do not guarantee future results.
          </p>
        </div>

        {!unlocked && (
          <div className="space-y-4">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 text-primary mb-3">
                <Lock className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-heading font-bold mb-1">
                Unlock Your Cash Flow Summary
              </h3>
              <p className="text-sm text-muted-foreground max-w-xs mx-auto">
                Enter your contact info to reveal your full annual net cash
                flow, owner payout, and initial investment breakdown.
              </p>
            </div>
            <Button
              onClick={() => setOpen(true)}
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 py-5 text-base font-bold"
            >
              <Lock className="w-4 h-4 mr-2" /> Unlock My Results
            </Button>
          </div>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Unlock Your Results</DialogTitle>
            <DialogDescription>
              Enter your contact info to see your full cash-flow breakdown.
            </DialogDescription>
          </DialogHeader>
          <LeadGateForm
            onUnlock={() => {
              unlock();
              setOpen(false);
            }}
            title=""
            subtitle=""
            ctaLabel="Unlock My Results"
            variant="inline"
          />
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default CashFlowSummary;
